import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TopBarHeader from './TopBarHeader';
import TopBarMiddle from './TopBarMiddle';
import TopBarBottom from './TopBarBottom';
import { AmplifierData, AmplifierProjectData, ProgramStatusMessage, HamlibControlRequestMessage, type AuditLogEntry, type UpdateAlarmLogStatusMessage } from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';

interface TopBarProps {
    isConnected: boolean;
    isMcuConnected: boolean;
    onAntennaChange: (mode: 'manual' | 'auto') => void;
    onTriggerModeChange: (mode: 'PTT1' | 'PP2' | '输入功率触发') => void;
    onLanguageChange: (lang: 'zh' | 'en' | 'jp') => void;
    triggerMode?: number;
    /** 嵌入其它页面（如无线电远程覆盖层）时传入，优先于路由 state */
    deviceIdOverride?: string;
}

export const TopBar: React.FC<TopBarProps> = ({
    isConnected,
    isMcuConnected,
    onAntennaChange,
    onTriggerModeChange,
    onLanguageChange,
    triggerMode,
    deviceIdOverride,
}) => {
    const subscriberKeyRef = useRef(`topbar-${Math.random().toString(36).slice(2)}`);
    const location = useLocation();
    const locationState = location.state as any;
    const deviceId =
        (deviceIdOverride != null && String(deviceIdOverride).trim() !== '')
            ? String(deviceIdOverride).trim()
            : (locationState?.deviceId || 'fallback-amplifier-device');
    const source = locationState?.source || 'unknown';
    const timestamp = locationState?.timestamp || Date.now();

    // 确保deviceId存在，如果不存在则使用默认值或提示用户
    useEffect(() => {
        console.log('Index组件 - 进入页面，执行设备ID检查');
        console.log('Index组件 - location.state:', locationState);
        console.log('Index组件 - 获取到的设备ID:', deviceId);
        console.log('Index组件 - 设备ID来源:', source);
        console.log('Index组件 - 时间戳:', timestamp);
        console.log('Index组件 - location:', location);
        if (!deviceId) {
            console.warn('设备ID获取失败，可能导致部分功能无法正常工作');
            // 可以在这里添加用户提示，或者使用默认设备ID
        }
    }, [locationState]);

    // 组件挂载时执行
    useEffect(() => {
        console.log('Index组件 - 组件挂载');
    }, []);

    // 确保组件渲染
    console.log('Index组件 - 渲染中，设备ID:', deviceId);


    const [isAntenna1On, setIsAntenna1On] = useState(false);
    const [isAntenna2On, setIsAntenna2On] = useState(false);
    const [triggerModeNumber, setTriggerModeNumber] = useState<number>(0);
    const [amplifierData, setAmplifierData] = useState<AmplifierData | null>(null);
    const [antennaMode, setAntennaMode] = useState<'manual' | 'auto'>('manual');
    const [programStatusMessage, setProgramStatusMessage] = useState<ProgramStatusMessage | null>(null);
    const [hamlibControlRequestMessage, setHamlibControlRequestMessage] = useState<HamlibControlRequestMessage | null>(null);
    const [auditLogEntries, setAuditLogEntries] = useState<AuditLogEntry[]>([]);
    const [amplifierProjectData, setAmplifierProjectData] = useState<AmplifierProjectData>({
        voltageCalibrationCoefficient: 0,
        currentCalibrationCoefficient: 0,
        frequencyMeasurementCalibrationCoefficient: 0,
        protectionVoltage: 0,
        protectionCurrent: 0,
        protectionTemperature: 0,
        protectionInputPowerPositive: 0,
        protectionInputPowerNegative: 0,
        protectionOutputPowerPositive: 0,
        protectionOutputPowerNegative: 0,
        protectionRf: 0,
        protectionFrequencyError: 0,
        rfTuneValue: 0,
        powerTriggerPttVoltage: 0,
        inputPowerPositiveCompareVoltage: 0,
        inputPowerNegativeCompareVoltage: 0,
        powerInputPowerPositiveCompareVoltage: 0,
        powerOutputPowerPositiveCompareVoltage: 0,
        outputPowerPositiveCompareVoltage: 0,
        outputPowerNegativeCompareVoltage: 0,
        overCurrentCompareVoltage: 0,
        bandParam: {
            vgs1: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs2: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs3: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs4: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs5: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs6: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs7: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs8: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs9: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs10: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs11: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs12: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs13: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs14: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs15: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs16: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 },
            vgs17: { frequency: 0, vgsPower: 0, inputPositive: 0, inputNegative: 0, outputPositive: 0, outputNegative: 0 }
        }
    });

    // 订阅设备状态，获取 AmplifierData 数据并转换为 AmplifierProjectData，同时获取程序状态消息和Hamlib控制指令
    useEffect(() => {
        console.log('Index组件 - 开始订阅设备状态');
        const subscriberKey = subscriberKeyRef.current;

        // 订阅设备状态
        amplifierMqttService.subscribeToDeviceStatus(
            deviceId,
            (data: AmplifierData) => {
                // 保存完整的 AmplifierData
                setAmplifierData(data);

                // 转换 AmplifierData 为 AmplifierProjectData
                const projectData: AmplifierProjectData = {
                    voltageCalibrationCoefficient: data.voltageCalibrationCoefficient,
                    currentCalibrationCoefficient: data.currentCalibrationCoefficient,
                    frequencyMeasurementCalibrationCoefficient: data.frequencyMeasurementCalibrationCoefficient,
                    protectionVoltage: data.protectionVoltage,
                    protectionCurrent: data.protectionCurrent,
                    protectionTemperature: data.protectionTemperature,
                    protectionInputPowerPositive: data.protectionInputPowerPositive,
                    protectionInputPowerNegative: data.protectionInputPowerNegative,
                    protectionOutputPowerPositive: data.protectionOutputPowerPositive,
                    protectionOutputPowerNegative: data.protectionOutputPowerNegative,
                    protectionRf: data.protectionRf,
                    protectionFrequencyError: data.protectionFrequencyError,
                    rfTuneValue: data.rfTuneValue,
                    powerTriggerPttVoltage: data.powerTriggerPttVoltage,
                    inputPowerPositiveCompareVoltage: data.inputPowerPositiveCompareVoltage,
                    inputPowerNegativeCompareVoltage: data.inputPowerNegativeCompareVoltage,
                    powerInputPowerPositiveCompareVoltage: data.powerInputPowerPositiveCompareVoltage,
                    powerOutputPowerPositiveCompareVoltage: data.powerOutputPowerPositiveCompareVoltage,
                    outputPowerPositiveCompareVoltage: data.outputPowerPositiveCompareVoltage,
                    outputPowerNegativeCompareVoltage: data.outputPowerNegativeCompareVoltage,
                    overCurrentCompareVoltage: data.overCurrentCompareVoltage,
                    bandParam: data.bandParam
                };

                console.log('Index组件 - 转换后的工程参数数据:', projectData);
                setAmplifierProjectData(projectData);
            },
            (programStatusMessage: ProgramStatusMessage) => {

                setProgramStatusMessage(programStatusMessage);
            },
            (hamlibControlRequestMessage: HamlibControlRequestMessage) => {

                setHamlibControlRequestMessage(hamlibControlRequestMessage);
                console.log('Index组件 - 接收到Hamlib控制指令:', hamlibControlRequestMessage);
            },
            (auditLogMessage: UpdateAlarmLogStatusMessage) => {
                const list = Array.isArray(auditLogMessage.data?.list) ? auditLogMessage.data.list : [];
                setAuditLogEntries(list);
            },
            subscriberKey
        );

        // 组件卸载时取消订阅
        return () => {
            console.log('Index组件 - 取消订阅设备状态');
            amplifierMqttService.unsubscribeFromDeviceStatus(deviceId, subscriberKey);
        };
    }, [deviceId]);

    useEffect(() => {
        if (triggerMode !== undefined) {
            const modeMap: { [key: number]: number } = {
                0: 0,
                1: 1,
                2: 2,
                3: 3
            };
            setTriggerModeNumber(modeMap[triggerMode] || 0);
        }
    }, [triggerMode]);

    // 触发模式以设备为准：从 MQTT 的 amplifierData 同步，不下发时不做乐观更新
    useEffect(() => {
        if (amplifierData?.triggerMode !== undefined) {
            setTriggerModeNumber(amplifierData.triggerMode);
        }
    }, [amplifierData?.triggerMode]);

    const handleTriggerModeChange = (mode: 'PTT1' | 'PP2' | '输入功率触发') => {
        onTriggerModeChange(mode);
    };

    const handleAntennaChange = (mode: 'manual' | 'auto') => {
        setAntennaMode(mode);
        onAntennaChange(mode);
    };

    const handleAntenna1Change = (isOn: boolean) => {
        setIsAntenna1On(isOn);
        if (isOn) {
            setIsAntenna2On(false);
            // 移除输入天线对触发模式的自动设置
        }
    };

    const handleAntenna2Change = (isOn: boolean) => {
        setIsAntenna2On(isOn);
        if (isOn) {
            setIsAntenna1On(false);
            // 移除输入天线对触发模式的自动设置
        }
    };

    return (
        <div>
            <TopBarHeader
                isConnected={isConnected}
                isMcuConnected={isMcuConnected}
                onAntennaChange={handleAntennaChange}
                onTriggerModeChange={handleTriggerModeChange}
                onLanguageChange={onLanguageChange}
                triggerMode={triggerModeNumber}
                amplifierProjectData={amplifierProjectData}
                deviceId={deviceId}
                amplifierData={amplifierData}
                programStatusMessage={programStatusMessage}
                hamlibControlRequestMessage={hamlibControlRequestMessage}
                auditLogEntries={auditLogEntries}
            />
            <TopBarMiddle
                deviceId={deviceId}
                amplifierData={amplifierData}
                programStatusMessage={programStatusMessage}
                hamlibControlRequestMessage={hamlibControlRequestMessage}
            />
            <TopBarBottom
                deviceId={deviceId}
                amplifierData={amplifierData}
                antennaMode={antennaMode}
            />
        </div>
    );
};

export default TopBar;